/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon } from '@/components/Icon';
import { Alert, Button, toast } from '@/design-system';
import { ShellDialog } from './ShellDialog';

/** Editable profile fields, persisted client-side keyed by user. */
interface ProfileForm {
  first: string;
  last: string;
  phone: string;
}

const EMPTY: ProfileForm = {
  first: '',
  last: '',
  phone: '',
};

const CONTACT_KEYS: (keyof ProfileForm)[] = ['first', 'last', 'phone'];

const AUTOCOMPLETE: Record<keyof ProfileForm, string> = {
  first: 'given-name',
  last: 'family-name',
  phone: 'tel',
};

const storeKey = (id?: string | null) => `ediag.profile.${id ?? 'anon'}`;
/** Phone is session-scoped (not localStorage) to avoid long-lived PII on disk. */
const phoneKey = (id?: string | null) => `ediag.profile.phone.${id ?? 'anon'}`;

function loadProfile(id: string | null | undefined, seed: Partial<ProfileForm>): ProfileForm {
  let saved: Partial<ProfileForm> = {};
  let phone = '';
  try {
    const raw = localStorage.getItem(storeKey(id));
    if (raw) saved = JSON.parse(raw) as Partial<ProfileForm>;
  } catch {
    /* storage unavailable - fall back to seed */
  }
  try {
    phone = sessionStorage.getItem(phoneKey(id)) ?? '';
  } catch {
    /* session storage unavailable */
  }
  // Ignore any phone that may have been written to localStorage by older builds.
  const nameFields: Partial<ProfileForm> = {};
  if (typeof saved.first === 'string') nameFields.first = saved.first;
  if (typeof saved.last === 'string') nameFields.last = saved.last;
  return { ...EMPTY, ...seed, ...nameFields, phone };
}

/** Profile dialog: read-only identity + editable contact details (manufacturing). */
export function ProfileModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { account, name, initials, mode } = useAuth();

  const seed = useMemo<Partial<ProfileForm>>(() => {
    const [first = '', ...rest] = (name ?? '').trim().split(/\s+/);
    return { first, last: rest.join(' ') };
  }, [name]);

  const [form, setForm] = useState<ProfileForm>(() => loadProfile(account?.id, seed));
  const set = (key: keyof ProfileForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const method = mode === 'mock' ? t('profile.authMock') : t('profile.authEntra');
  // UPN local part is the GM login id.
  const loginId = account?.username?.split('@')[0]?.toLowerCase() ?? '';
  const email = account?.username ?? '';

  const accountRows: [string, string][] = [
    account?.gmin ? ['GMIN', account.gmin] : null,
    loginId ? [t('profile.labels.userId'), loginId] : null,
  ].filter(Boolean) as [string, string][];

  const missing = CONTACT_KEYS.filter((k) => !form[k].trim());

  const save = () => {
    try {
      const { first, last, phone } = form;
      localStorage.setItem(storeKey(account?.id), JSON.stringify({ first, last }));
      sessionStorage.setItem(phoneKey(account?.id), phone);
    } catch {
      /* non-fatal */
    }
    toast.success(t('toast.profileSaved'));
    onClose();
  };

  const field = (label: string, key: keyof ProfileForm, placeholder?: string) => {
    const empty = !form[key].trim();
    return (
      <label className={empty ? 'pf-field pf-miss' : 'pf-field'}>
        <span className="pf-label">
          {label}
          {empty && <span className="pf-req">{t('profile.required')}</span>}
        </span>
        <input
          className="pf-in"
          placeholder={placeholder ?? ''}
          value={form[key]}
          onChange={set(key)}
          autoComplete={AUTOCOMPLETE[key]}
          type={key === 'phone' ? 'tel' : 'text'}
        />
      </label>
    );
  };

  const displayName = [form.first, form.last].filter(Boolean).join(' ') || name || '\u2014';

  return (
    <ShellDialog
      onClose={onClose}
      icon="person"
      title={t('profile.title')}
      size="lg"
      flush
      className="prof-modal"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button type="button" onClick={save}>
            <Icon name="check" size={16} /> {t('profile.saveProfile')}
          </Button>
        </>
      }
    >
      <div className="pf-cols">
        <div className="pf-left">
          <div className="pf-hero2">
            <span className="prof-av" aria-hidden="true">
              {initials || 'JT'}
            </span>
            <div className="prof-who">
              <div className="prof-name">{displayName}</div>
              <div className="prof-mail">{email || '\u2014'}</div>
              <span className="prof-role">{method}</span>
            </div>
          </div>
          <h3 className="pf-sec">{t('profile.account')}</h3>
          <dl className="pf-readlist">
            {accountRows.map(([k, v]) => (
              <div className="pf-readrow" key={k}>
                <dt className="pf-rk">{k}</dt>
                <dd className="pf-rv">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="pf-right">
          {missing.length > 0 && (
            <Alert variant="info" className="pf-alert">
              {t('profile.missingAlert', {
                count: missing.length,
                suffix: missing.length > 1 ? 's' : '',
              })}
            </Alert>
          )}
          <h3 className="pf-sec">{t('profile.contactDetails')}</h3>
          <div className="pf-grid">
            {field(t('profile.firstName'), 'first')}
            {field(t('profile.lastName'), 'last')}
            {field(t('profile.phoneNumber'), 'phone', t('profile.phonePlaceholder'))}
            <label className="pf-field">
              <span className="pf-label">{t('profile.email')}</span>
              <input className="pf-in" value={email} readOnly aria-readonly="true" />
            </label>
          </div>
        </div>
      </div>
    </ShellDialog>
  );
}
